import { useEffect, useRef } from 'react';

const SERVICES = [
  {
    id: 1,
    ko: '반영구 눈썹 문신',
    en: 'Semi-Permanent Eyebrow',
    zh: '半永久纹眉',
    ja: '半永久眉アート',
    badge: '쑥 좌욕 포함',
    badgeColor: 'bg-plum',
    price: 250000,
    icon: 'ri-contrast-2-line',
    detail: 'Mugwort Steaming Included',
    highlight: true,
  },
  {
    id: 2,
    ko: '역노화 레이저',
    en: 'Reverse Aging Laser',
    zh: '逆衰老激光',
    ja: '逆エイジングレーザー',
    badge: 'MICRO-JET',
    badgeColor: 'bg-gold',
    price: 250000,
    icon: 'ri-flashlight-line',
    detail: 'FGF+EGF 물광피부',
    highlight: false,
  },
  {
    id: 3,
    ko: '안신환 + 청뇌환',
    en: 'Herbal Medicine (Mind)',
    zh: '安神丸+清脑丸',
    ja: '安神丸+清脳丸',
    badge: '한의학 박사 처방',
    badgeColor: 'bg-plum-dark',
    price: 350000,
    icon: 'ri-mental-health-line',
    detail: '불안·불면·두통 개선',
    highlight: false,
  },
  {
    id: 4,
    ko: '슬림 다이어트 한약',
    en: 'Slim Diet Herbal',
    zh: '减肥瘦身韩药',
    ja: 'スリムダイエット漢薬',
    badge: '체질 맞춤',
    badgeColor: 'bg-gold-dark',
    price: 300000,
    icon: 'ri-seedling-line',
    detail: '지방분해 · 숙변제거',
    highlight: false,
  },
  {
    id: 5,
    ko: '아이돌 메이크업',
    en: 'Idol Make-up',
    zh: '爱豆化妆',
    ja: 'アイドルメイク',
    badge: '글램 스타일',
    badgeColor: 'bg-plum-light',
    price: 100000,
    icon: 'ri-star-smile-line',
    detail: '100,000원부터~',
    highlight: false,
    consultation: false,
  },
  {
    id: 6,
    ko: '두피 문신',
    en: 'Scalp Micropigmentation',
    zh: '头皮纹身 · SMP',
    ja: 'スカルプマイクロピグメンテーション',
    badge: 'SMP',
    badgeColor: 'bg-gray-700',
    price: 0,
    icon: 'ri-user-3-line',
    detail: '탈모·헤어라인 자연스럽게 커버',
    highlight: false,
    consultation: true,
  },
  {
    id: 7,
    ko: '아이라인 문신',
    en: 'Eyeliner Tattoo',
    zh: '眼线文身',
    ja: 'アイラインタトゥー',
    badge: '반영구',
    badgeColor: 'bg-plum',
    price: 0,
    icon: 'ri-eye-line',
    detail: '자연스러운 눈매 교정 · 반영구 케어',
    highlight: false,
    consultation: true,
  },
  {
    id: 8,
    ko: '입술 문신',
    en: 'Lip Tattoo',
    zh: '唇部文身',
    ja: 'リップタトゥー',
    badge: '반영구',
    badgeColor: 'bg-[#C0606A]',
    price: 0,
    icon: 'ri-emotion-line',
    detail: '볼륨감 있는 입술 컬러·라인 시술',
    highlight: false,
    consultation: true,
  },
];

function formatPrice(price: number) {
  return price.toLocaleString('ko-KR') + '원';
}

export default function PricingSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08 }
    );
    const els = ref.current?.querySelectorAll('.section-reveal, .section-reveal-scale');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pricing" ref={ref} className="bg-cream py-20 px-7 md:py-28 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20 section-reveal">
          <span className="inline-block text-gold font-sans text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">Event Price · 活动价格 · イベント価格</span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 font-bold mb-4">이벤트 가격표</h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mb-5"></div>
          <p className="font-sans text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
            지금 바로 특별 이벤트 가격으로 프리미엄 서비스를 경험해보세요.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 bg-plum/10 border border-plum/20 text-plum px-4 py-2 rounded-full text-xs sm:text-sm font-sans">
            <i className="ri-time-line"></i>
            한정 기간 이벤트 · Limited Time Event Prices
          </div>
        </div>

        {/* Cards Grid — each card staggered */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
          {SERVICES.map((s, i) => (
            <div
              key={s.id}
              className={`section-reveal-scale relative rounded-2xl p-5 sm:p-6 border transition-all duration-300 hover:-translate-y-2 hover:shadow-lg ${
                s.highlight
                  ? 'bg-gradient-to-br from-plum to-plum-light text-white border-plum'
                  : 'bg-white border-gold/15 hover:border-gold/40'
              }`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              {/* Badge */}
              <div className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-sans font-semibold mb-4 whitespace-nowrap ${
                s.highlight ? 'bg-white/20 text-white' : `${s.badgeColor} text-white`
              }`}>
                <i className="ri-price-tag-3-line"></i>
                {s.badge}
              </div>

              {/* Icon */}
              <div className={`w-12 h-12 flex items-center justify-center rounded-full mb-4 ${
                s.highlight ? 'bg-white/20 text-white' : 'bg-gold-pale text-gold'
              }`}>
                <i className={`${s.icon} text-2xl`}></i>
              </div>

              {/* Name */}
              <h3 className={`font-serif text-xl font-bold mb-1 ${s.highlight ? 'text-white' : 'text-gray-900'}`}>
                {s.ko}
              </h3>
              <p className={`font-sans text-xs mb-0.5 ${s.highlight ? 'text-white/80' : 'text-gold-dark'}`}>{s.en}</p>
              <p className={`font-sans text-xs mb-4 ${s.highlight ? 'text-white/60' : 'text-gray-400'}`}>
                {s.zh} · {s.ja}
              </p>

              {/* Detail */}
              <p className={`font-sans text-sm mb-5 ${s.highlight ? 'text-white/80' : 'text-gray-500'}`}>
                {s.detail}
              </p>

              {/* Price */}
              <div className={`border-t pt-4 flex items-end justify-between ${s.highlight ? 'border-white/20' : 'border-gold/15'}`}>
                <div>
                  <p className={`font-sans text-xs mb-0.5 ${s.highlight ? 'text-white/60' : 'text-gray-400'}`}>
                    {s.consultation ? '가격 안내' : '이벤트가 · Event Price'}
                  </p>
                  {s.consultation ? (
                    <p className={`font-serif text-lg sm:text-xl font-bold ${s.highlight ? 'text-gold-shine' : 'text-plum'}`}>
                      가격 상담
                    </p>
                  ) : (
                    <p className={`font-serif text-xl sm:text-2xl font-bold ${s.highlight ? 'text-gold-shine' : 'text-plum'}`}>
                      {s.id === 5 ? '100,000원~' : formatPrice(s.price)}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => { const el = document.querySelector('#reservation'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                  className={`px-4 py-2 rounded-full text-sm font-sans font-semibold whitespace-nowrap cursor-pointer transition-colors duration-200 ${
                    s.highlight
                      ? 'bg-gold text-white hover:bg-gold-dark'
                      : 'bg-plum text-white hover:bg-plum-dark'
                  }`}
                >
                  {s.consultation ? '상담' : '예약'}
                </button>
              </div>
            </div>
          ))}

          {/* Notice Card */}
          <div
            className="section-reveal-scale bg-gold-pale border border-gold/30 rounded-2xl p-5 sm:p-6 flex flex-col justify-center"
            style={{ transitionDelay: `${SERVICES.length * 0.1}s` }}
          >
            <div className="w-10 h-10 flex items-center justify-center text-gold mb-4">
              <i className="ri-information-line text-2xl"></i>
            </div>
            <h4 className="font-serif text-lg font-bold text-gray-900 mb-3">상담 후 맞춤 안내</h4>
            <p className="font-sans text-sm text-gray-600 leading-relaxed mb-4">
              모든 서비스는 개인 체질과 상태에 따라 가격이 달라질 수 있습니다. 무료 상담 후 정확한 가격을 안내해드립니다.
            </p>
            <a
              href="tel:01067404061"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-plum text-white px-5 py-2.5 rounded-full text-sm font-sans font-semibold whitespace-nowrap cursor-pointer hover:bg-plum-dark transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line"></i></span>
              무료 상담 전화
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
